import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * The kit's button (UX Stage B, B3). Four variants, two sizes, a loading state and an icon slot, on the tokens.
 * The rule: ONE primary per view (the thing the page is for); everything else is secondary or ghost; destructive
 * only for what deletes, writes off or reverses. See docs/ux/COMPONENTS.md.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'ghost';
export type ButtonSize = 'sm' | 'md';

export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-mp-accent text-mp-accent-contrast border-transparent hover:opacity-90',
  secondary: 'bg-mp-surface text-mp-text border-mp-border hover:bg-mp-surface-2',
  destructive: 'bg-mp-danger text-mp-danger-contrast border-transparent hover:opacity-90',
  ghost: 'bg-transparent text-mp-accent border-transparent hover:bg-mp-surface-2',
};

const SIZES: Record<ButtonSize, { cls: string; minHeight: string }> = {
  sm: { cls: 'px-mp-3 text-mp-sm gap-mp-1', minHeight: 'max(24px, calc(var(--mp-row-h) - 16px))' },
  md: { cls: 'px-mp-4 text-mp-base gap-mp-2', minHeight: 'max(28px, calc(var(--mp-row-h) - 8px))' },
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Busy: a spinner in place of the icon, the label kept, and the button disabled until it finishes. */
  loading?: boolean;
  /** An icon before the label (a lucide icon at h-4 w-4). For an icon-only button, give an aria-label. */
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
}

const BASE = 'inline-flex items-center justify-center whitespace-nowrap rounded-mp-sm border font-medium transition '
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-mp-accent focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50';

/** A Button's look for something that must be a link (<a>, <Link>): the same size, padding and variant. Pair it with
 *  `style={buttonStyle(size)}` for the matching height. */
export const buttonClass = (variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className = '') =>
  cn(BASE, SIZES[size].cls, BUTTON_VARIANTS[variant], className);
export const buttonStyle = (size: ButtonSize = 'md'): React.CSSProperties => ({ minHeight: SIZES[size].minHeight });

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'secondary', size = 'md', loading = false, icon, iconRight, disabled, className = '', style, children, type = 'button', ...rest }, ref) => {
    const s = SIZES[size];
    return (
      <button ref={ref} type={type} {...rest} disabled={disabled || loading} aria-busy={loading || undefined}
              data-variant={variant}
              style={{ minHeight: s.minHeight, ...style }}
              // cn (tailwind-merge): a caller's className wins over the variant's (e.g. a ghost button in text-mp-danger)
              className={buttonClass(variant, size, className)}>
        {loading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden /> : icon && <span className="inline-flex shrink-0" aria-hidden>{icon}</span>}
        {children}
        {iconRight && <span className="inline-flex shrink-0" aria-hidden>{iconRight}</span>}
      </button>
    );
  },
);
Button.displayName = 'Button';
